<template>
	<div class="table-overflow-hide">
		<div class="directoryTable scrollable-55vh">
			<table class="table" data-cy="trials-table">
				<thead class="sticky-header">
					<tr>
						<th @click="sortBy('trial_id')" class="sortable-header">
							Trial ID
							<span v-if="sortKey === 'trial_id'" class="sort-indicator">
								<v-icon size="small">
									{{ sortOrder === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
								</v-icon>
							</span>
						</th>
						<th @click="sortBy('school_name')" class="sortable-header">
							School Name
							<span v-if="sortKey === 'school_name'" class="sort-indicator">
								<v-icon size="small">
									{{ sortOrder === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
								</v-icon>
							</span>
						</th>
						<th @click="sortBy('district_name')" class="sortable-header">
							District
							<span v-if="sortKey === 'district_name'" class="sort-indicator">
								<v-icon size="small">
									{{ sortOrder === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
								</v-icon>
							</span>
						</th>
						<th @click="sortBy('email')" class="sortable-header">
							Email
							<span v-if="sortKey === 'email'" class="sort-indicator">
								<v-icon size="small">
									{{ sortOrder === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
								</v-icon>
							</span>
						</th>
						<th @click="sortBy('created_tstamp')" class="sortable-header">
							Created Date
							<span v-if="sortKey === 'created_tstamp'" class="sort-indicator">
								<v-icon size="small">
									{{ sortOrder === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
								</v-icon>
							</span>
						</th>
						<th @click="sortBy('status')" class="sortable-header">
							Status
							<span v-if="sortKey === 'status'" class="sort-indicator">
								<v-icon size="small">
									{{ sortOrder === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
								</v-icon>
							</span>
						</th>
						<th @click="sortBy('demo_date')" class="sortable-header">
							Demo Date
							<span v-if="sortKey === 'demo_date'" class="sort-indicator">
								<v-icon size="small">
									{{ sortOrder === 'asc' ? 'mdi-arrow-up' : 'mdi-arrow-down' }}
								</v-icon>
							</span>
						</th>
						<th class="icon-col" style="text-align: right;">
							<v-btn
								@click="reload"
								icon
								color="gray200"
								size="x-small"
								:loading="trialsStore.loading"
								style="max-width: 20px; max-height: 20px"
								data-cy="reload-trials-btn"
							>
								<v-icon>mdi-reload</v-icon>
							</v-btn>
						</th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="trial in paginatedResults" :key="trial.trial_id" class="table-row">
						<!-- Trial ID first — clickable to open edit drawer -->
						<td>
							<span
								class="trial-id-link"
								@click="editTrial(trial)"
								:data-cy="`trial-id-link-${trial.trial_id}`"
								:title="'Edit ' + trial.trial_id"
							>{{ trial.trial_id }}</span>
						</td>
						<td>{{ trial.school_name }}</td>
						<td>{{ trial.district_name }}</td>
						<td>{{ trial.email }}</td>
						<td>{{ formatDate(trial.created_tstamp) }}</td>
						<td>
							<v-chip
								:color="getStatusColor(trial.status)"
								size="small"
								class="cursor-pointer"
								@click="openStatusModal(trial)"
								:data-cy="`status-chip-${trial.trial_id}`"
							>
								{{ trial.status }}
							</v-chip>
						</td>
						<!-- Demo Date: chip if scheduled, calendar icon if not -->
						<td>
							<v-chip
								v-if="trial.demo_date"
								color="teal"
								size="small"
								class="cursor-pointer"
								@click="openDemoScheduleModal(trial)"
								:data-cy="`demo-date-chip-${trial.trial_id}`"
								:title="'Reschedule demo'"
							>
								{{ formatDemoDate(trial.demo_date) }} ET
							</v-chip>
							<v-btn
								v-else
								density="compact"
								icon
								variant="text"
								@click="openDemoScheduleModal(trial)"
								:data-cy="`schedule-demo-btn-${trial.trial_id}`"
								title="Schedule demo"
							>
								<v-icon color="gray500">mdi-calendar-plus</v-icon>
							</v-btn>
						</td>
						<td class="actions-cell">
							<v-btn-group density="compact" variant="outlined" divided>
								<v-btn
									size="small"
									:loading="quoteGeneratingId === trial.trial_id"
									:disabled="quoteGeneratingId !== null"
									@click="openQuoteGenerateModal(trial)"
									:data-cy="`generate-quote-btn-${trial.trial_id}`"
									title="Generate quote"
									prepend-icon="mdi-file-document-plus"
								>
									Quote
								</v-btn>
								<v-btn
									size="small"
									:loading="quoteDownloadingId === trial.trial_id"
									:disabled="quoteDownloadingId !== null"
									@click="downloadQuote(trial)"
									:data-cy="`download-quote-btn-${trial.trial_id}`"
									title="Download quote"
									icon
								>
									<v-icon>mdi-download</v-icon>
								</v-btn>
							</v-btn-group>
						</td>
					</tr>
				</tbody>
			</table>
			<div
				v-if="trialsStore.loading && !trialsStore.trials.length"
				style="
					height: calc(100% - 60px);
					background-color: rgba(246, 247, 237, 0.5);
					border-radius: 15px;
				"
			>
				<LoadingState text="Loading Trials"></LoadingState>
			</div>
			<div
				v-if="!trialsStore.loading && !trialsStore.trials.length"
				style="
					height: calc(100% - 60px);
					background-color: rgba(246, 247, 237, 0.5);
					border-radius: 15px;
				"
			>
				<div class="empty-state">
					<figure style="max-width: 300px">
						<IconListEmptyState />
					</figure>
					<div class="title">No trials found.</div>
					<p>Try adjusting your search or create a new trial.</p>
				</div>
			</div>
		</div>
	</div>
	<div class="table-pagination-btns">
		<pagination
			v-model:curr-page="currPage"
			v-model:per-page="perPage"
			:page-status-text="pageStatusText"
			:total-pages="totalPages"
			@page-down="pageDown"
			@page-up="pageUp"
		></pagination>
	</div>

	<!-- Edit Modal -->
	<TrialEditModal v-model="showEditModal" :trial="selectedTrial" />
	<TrialStatusModal v-model="showStatusModal" :trial="selectedStatusTrial" />
	<QuoteGenerateModal
		v-model="showQuoteGenerateModal"
		:trial="selectedTrialForQuote"
		:loading="quoteGeneratingId !== null"
		@submit="onQuoteGenerateSubmit"
	/>
	<DemoScheduleModal v-model="showDemoScheduleModal" :trial="selectedDemoTrial" />
</template>

<script setup lang="ts">
import { computed, watch, onMounted, ref } from "vue";
import usePaginate from "@/hooks/usePaginate";
import pagination from "@/components/pagination.vue";
import { complexSearch } from "@/utils";
import LoadingState from "@/components/loadingState.vue";
import IconListEmptyState from "@/assets/icons/list-empty-state.svg";
import dayjs from "dayjs";
import FetchService from "@/services/fetch.service";
import { useAlertsStore } from "@/stores/alerts";
import { useAuthenticationStore } from "@/stores/authentication";
import useTrials, { type TrialMembership } from "@/stores/support/trials";
import TrialEditModal from "@/components/support/TrialEditModal.vue";
import TrialStatusModal from "@/components/support/TrialStatusModal.vue";
import QuoteGenerateModal from "@/components/support/QuoteGenerateModal.vue";
import DemoScheduleModal from "@/components/support/DemoScheduleModal.vue";

const props = defineProps<{
  search: string;
  status: string;
}>();

defineEmits<{
  (e: 'update:search', ...args: any[]): void
}>();

const trialsStore = useTrials();
const alertsStore = useAlertsStore();

// Quote actions loading state (one at a time per type)
const quoteGeneratingId = ref<string | null>(null);
const quoteDownloadingId = ref<string | null>(null);

// Quote generate modal state
const showQuoteGenerateModal = ref(false);
const selectedTrialForQuote = ref<TrialMembership | null>(null);

// Edit modal state
const showEditModal = ref(false);
const selectedTrial = ref<TrialMembership | null>(null);
const showStatusModal = ref(false);
const selectedStatusTrial = ref<TrialMembership | null>(null);

// Demo schedule modal state
const showDemoScheduleModal = ref(false);
const selectedDemoTrial = ref<TrialMembership | null>(null);

// Sorting state
const sortKey = ref<string>('');
const sortOrder = ref<'asc' | 'desc'>('asc');

// Load trials on component mount
onMounted(() => {
  trialsStore.loadTrials({
    status: props.status === 'demo' ? 'all' : props.status,
  });
});

// Watch for status changes and reload trials (search is handled client-side)
watch(() => props.status, (newStatus) => {
  // 'demo' is a client-side filter — load all trials then filter locally
  trialsStore.loadTrials({
    status: newStatus === 'demo' ? 'all' : newStatus,
  });
});

const filteredData = computed(() => {
  let filtered = trialsStore.trials.filter((trial) => {
    // 'demo' is a client-side filter: show only trials that have a demo_date set
    if (props.status === 'demo') {
      if (!trial.demo_date) return false;
    }

    // Client-side search
    if (props.search) {
      return (
        complexSearch(props.search, trial.school_name) ||
        complexSearch(props.search, trial.district_name) ||
        complexSearch(props.search, trial.email) ||
        complexSearch(props.search, trial.username) ||
        complexSearch(props.search, trial.firstname) ||
        complexSearch(props.search, trial.lastname)
      );
    }
    return true;
  });

  // Apply sorting if sortKey is set
  if (sortKey.value) {
    filtered = filtered.sort((a, b) => {
      let aValue = a[sortKey.value as keyof TrialMembership];
      let bValue = b[sortKey.value as keyof TrialMembership];

      // Handle undefined values
      if (aValue === undefined && bValue === undefined) return 0;
      if (aValue === undefined) return sortOrder.value === 'asc' ? 1 : -1;
      if (bValue === undefined) return sortOrder.value === 'asc' ? -1 : 1;

      // Handle date sorting
      if (sortKey.value === 'created_tstamp') {
        aValue = new Date(aValue as string).getTime();
        bValue = new Date(bValue as string).getTime();
      }

      // Handle string sorting (case-insensitive)
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) {
        return sortOrder.value === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortOrder.value === 'asc' ? 1 : -1;
      }
      return 0;
    });
  }

  return filtered;
});

const { currPage, perPage, pageStatusText, totalPages, pageDown, pageUp, paginatedResults } =
  usePaginate(filteredData);

const formatDate = (date: string) => {
  return dayjs(date).format('MM/DD/YYYY HH:mm');
};

// Shorter format for the demo date chip — no need for seconds
const formatDemoDate = (date: string) => {
  return dayjs(date).format('MM/DD/YY h:mma');
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'requested': return 'info';
    case 'fullfilled': return 'success'; // Note: database has typo 'fullfilled'
    case 'expired': return 'error';
    case 'cancelled': return 'error';
    case 'rejected': return 'error';
    case 'pending': return 'warning';
    default: return 'default';
  }
};

const sortBy = (key: string) => {
  if (sortKey.value === key) {
    // If clicking the same column, toggle order
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
  } else {
    // If clicking a different column, set it as the new sort key with ascending order
    sortKey.value = key;
    sortOrder.value = 'asc';
  }
};

const reload = () => {
  trialsStore.loadTrials({
    status: props.status
  });
};

const editTrial = (trial: TrialMembership) => {
  selectedTrial.value = { ...trial }; // Create a copy, not a reference
  showEditModal.value = true;
};

const openStatusModal = (trial: TrialMembership) => {
  selectedStatusTrial.value = { ...trial };
  showStatusModal.value = true;
};

const openQuoteGenerateModal = (trial: TrialMembership) => {
  selectedTrialForQuote.value = { ...trial };
  showQuoteGenerateModal.value = true;
};

const openDemoScheduleModal = (trial: TrialMembership) => {
  selectedDemoTrial.value = { ...trial };
  showDemoScheduleModal.value = true;
};

const onQuoteGenerateSubmit = async (payload: {
  trial_id: string;
  duration: number;
  blackbaud: boolean;
  discount?: number;
  notes: string;
}) => {
  quoteGeneratingId.value = payload.trial_id;
  try {
    // Ensure auth token is loaded from storage before request (avoids Unauthorized when token was restored from localStorage)
    useAuthenticationStore().loadToken();
    const res = await FetchService.fetchData("/quote/generate", "post", payload);
    quoteGeneratingId.value = null;
    if (res === false) {
      alertsStore.addAlert("error", "Session expired or unauthorized. Please log in again.");
      return;
    }
    if (res && (res as { success?: boolean }).success && (res as { file?: string }).file) {
      showQuoteGenerateModal.value = false;
      selectedTrialForQuote.value = null;
      alertsStore.addAlert(
        "success",
        "Quote generated successfully. Use Download quote to get the PDF."
      );
      return;
    }
    const errMsg =
      (res as { error?: string })?.error ||
      (res as { message?: string })?.message ||
      "Failed to generate quote.";
    alertsStore.addAlert("error", errMsg);
  } catch (e) {
    quoteGeneratingId.value = null;
    console.error("Generate quote error:", e);
    alertsStore.addAlert("error", "An unexpected error occurred while generating the quote.");
  }
};

const downloadQuote = async (trial: TrialMembership) => {
  quoteDownloadingId.value = trial.trial_id;
  try {
    useAuthenticationStore().loadToken();
    const res = await FetchService.fetchData(`/quote/${trial.trial_id}`, "get");
    quoteDownloadingId.value = null;
    if (res === false) {
      alertsStore.addAlert("error", "Session expired or unauthorized. Please log in again.");
      return;
    }
    const url = res && (res as { url?: string }).url;
    if (url) {
      window.open(url, "_blank", "noopener,noreferrer");
      alertsStore.addAlert("success", "Quote download started.");
      return;
    }
    const errMsg =
      (res as { error?: string })?.error ||
      (res as { message?: string })?.message ||
      "Failed to get download URL. Generate a quote first.";
    alertsStore.addAlert("error", errMsg);
  } catch (e) {
    quoteDownloadingId.value = null;
    console.error("Download quote error:", e);
    alertsStore.addAlert("error", "An unexpected error occurred while downloading the quote.");
  }
};
</script>

<style scoped lang="scss">
@use "@/assets/_vars.scss" as *;

.icon-col {
	min-width: 120px;
}

.actions-cell {
	white-space: nowrap;
}

.sortable-header {
	cursor: pointer;
	user-select: none;
	transition: background-color 0.2s;

	&:hover {
		background-color: rgba(0, 0, 0, 0.05);
	}
}

.sort-indicator {
	margin-left: 4px;
	display: inline-block;
}

.table-row {
	td {
		transition: background-color 0.3s;
	}
	&:hover {
		td {
			background-color: #f5f5f5;
		}
	}
}

.cursor-pointer {
	cursor: pointer;
}

.trial-id-link {
	font-family: monospace;
	font-size: 0.75rem;
	color: rgb(var(--v-theme-primary));
	cursor: pointer;
	text-decoration: underline;
	text-underline-offset: 2px;

	&:hover {
		opacity: 0.75;
	}
}
</style>
